/**
 * Keep the hybrid "remote" (system-audio) hunt from ever locking onto the
 * microphone.
 *
 * The remote channel is meant to capture the *far-end* participants. But virtual
 * audio mixers (SteelSeries Sonar, VoiceMeeter, VB-Cable, …) expose the mic as a
 * **render endpoint** — e.g. "SteelSeries Sonar - Microphone" — and often flag it
 * as the OS Communications default. The hunt prefers the Communications default
 * among active outputs, so the instant you speak that mic-loopback endpoint goes
 * active and wins, locking the "remote" channel onto your own voice instead of
 * the (louder) output the call is actually playing on.
 *
 * The fix is deterministic, not signal-based: an output is the microphone when
 *   - its name says so (a render endpoint called "…Microphone…/…Mic…" is a mic
 *     loopback, never where the far-end plays), or
 *   - it matches the user's selected input device by name (same physical/virtual
 *     device as the primary mic).
 * Such outputs are excluded from auto-detect (the manual picker can still choose
 * them if a user really wants to).
 */

/** Collapse whitespace + lowercase for tolerant name comparison. */
function normalizeDeviceName(name: string): string {
  return name.toLowerCase().replace(/\s+/g, " ").trim()
}

/** Strip the role qualifier Windows prepends to default endpoints. */
function stripRolePrefix(name: string): string {
  return name.replace(/^(default|communications)\s*-\s*/, "").trim()
}

/**
 * True when a render-endpoint output is actually the microphone, so the remote
 * far-end hunt must never auto-lock onto it.
 *
 * @param outputName Friendly name of the render endpoint (from `listTapTargets`).
 * @param micLabel   Label of the user's selected mic (getUserMedia device label);
 *                   undefined when unknown (then only the name-based test applies).
 */
export function isMicrophoneOutput(outputName: string, micLabel: string | undefined): boolean {
  const out = stripRolePrefix(normalizeDeviceName(outputName))
  if (!out) return false

  // A render endpoint that calls itself a microphone is a mic monitor/loopback.
  if (/\bmicrophones?\b/.test(out) || /\bmic\b/.test(out)) return true

  if (!micLabel) return false
  const mic = stripRolePrefix(normalizeDeviceName(micLabel))
  // Same device when one name fully contains the other's core identity. Guard on
  // length so a short token can't match a brand prefix shared by sibling
  // endpoints (e.g. "SteelSeries Sonar - Media" must NOT match the mic).
  if (out.length < 4 || mic.length < 4) return false
  return mic.includes(out) || out.includes(mic)
}

/**
 * Ids of the outputs that are the microphone and must be excluded from the remote
 * far-end auto-detect.
 */
export function micOutputIds(
  targets: ReadonlyArray<{ id: string; name: string }>,
  micLabel: string | undefined,
): Set<string> {
  const ids = new Set<string>()
  for (const t of targets) {
    if (isMicrophoneOutput(t.name, micLabel)) ids.add(t.id)
  }
  return ids
}
